/**
 * Pagina lista fatture del modulo documentale.
 *
 * Organizzazione a tabs "Tutte / Attive / Passive".
 * Ricerca LIBERA su numero, denominazioni, partite IVA e codici fiscali (param `q`).
 * Filtri secondari collassabili (stato, pagamento, date, importi, anno).
 * Elenco PAGINATO, con pagina e dimensione pagina nella URL.
 *
 * Il totale mostrato viene da GET /fatture/riepilogo, che aggrega TUTTO l'insieme
 * filtrato lato database. Prima veniva sommato dalle righe caricate ed etichettato
 * "Totale periodo": con 79 passive e una pagina da 50 la schermata dichiarava
 * 35.619,59 al posto di 54.145,51, e il numero si muoveva da solo a ogni import.
 * Non sommare mai le righe di questa pagina per ottenere un totale.
 *
 * Regola 6.8 CLAUDE.md: importa solo da @floraltech/ui.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Filter, Search } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import AbbinamentoAlert from '../components/AbbinamentoAlert';
import StatoBadge from '../components/StatoBadge';
import StatoPagamentoBadge from '../components/StatoPagamentoBadge';

interface Fattura {
  id: string;
  year: number;
  numero: string | null;
  data: string;
  tipo_documento_codice: string;
  direzione: 'attiva' | 'passiva';
  stato: string;
  cedente_denominazione: string | null;
  cessionario_denominazione: string | null;
  importo_totale: number | null;
  stato_pagamento: string | null;
  data_scadenza: string | null;
  abbinamento_stato: string;
  stato_sdi: string | null;
}

/** Totali dell'intero insieme filtrato, calcolati dal backend. */
interface Riepilogo {
  conteggio: number;
  totale_imponibile: number | string;
  totale_iva: number | string;
  totale_documenti: number | string;
}

type Direzione = 'tutte' | 'attiva' | 'passiva';

const ANNO_CORRENTE = new Date().getFullYear();

const DIMENSIONE_PAGINA_DEFAULT = 50;
// MAX_PAGE_SIZE del backend e' 200: non offrire scelte che verrebbero rifiutate.
const DIMENSIONI_PAGINA = [25, 50, 100, 200];

const fmtEur = (n: number | null): string =>
  n == null
    ? '—'
    : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n));

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const FattureList: React.FC = () => {
  const navigate = useNavigate();

  const [fatture, setFatture] = useState<Fattura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [direzioneRaw, setDirezione] = useUrlState('direzione', 'tutte');
  const direzione = direzioneRaw as Direzione;
  const [search, setSearch] = useUrlState('q', '');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [year, setYear] = useUrlNumberState('anno', ANNO_CORRENTE);
  const [stato, setStato] = useUrlState('stato', '__all__');
  const [statoPagamento, setStatoPagamento] = useUrlState('pag', '__all__');
  const [dataFrom, setDataFrom] = useUrlState('da', '');
  const [dataTo, setDataTo] = useUrlState('a', '');
  const [importoMin, setImportoMin] = useUrlState('min', '');
  const [importoMax, setImportoMax] = useUrlState('max', '');

  // Paginazione nella URL, come i filtri: tornando indietro da un dettaglio si
  // ritrova la stessa pagina, non la prima.
  const [pagina, setPagina] = useUrlNumberState('p', 1);
  const [perPagina, setPerPagina] = useUrlNumberState('n', DIMENSIONE_PAGINA_DEFAULT);

  // Il totale NON si calcola sommando le righe caricate: quella somma e' il
  // totale della pagina. Lo chiede il backend su tutto l'insieme filtrato.
  const [riepilogo, setRiepilogo] = useState<Riepilogo | null>(null);

  const searchPlaceholder = 'Cerca numero, cliente, fornitore, partita IVA, codice fiscale...';

  // Firma dei filtri (paginazione esclusa): se cambia, si torna a pagina 1,
  // altrimenti si resterebbe su una pagina che nel nuovo insieme non esiste.
  const firmaFiltri = [
    year, direzione, search.trim(), stato, statoPagamento,
    dataFrom, dataTo, importoMin, importoMax,
  ].join('|');
  const firmaPrecedente = useRef(firmaFiltri);
  useEffect(() => {
    if (firmaPrecedente.current !== firmaFiltri) {
      firmaPrecedente.current = firmaFiltri;
      if (pagina !== 1) setPagina(1);
    }
  }, [firmaFiltri, pagina, setPagina]);

  /** Parametri di filtro condivisi da elenco e riepilogo: un solo posto. */
  const parametriFiltro = useCallback(() => {
    const params = new URLSearchParams({ year: String(year) });
    if (direzione !== 'tutte') params.set('direzione', direzione);
    if (stato !== '__all__') params.set('stato', stato);
    if (statoPagamento !== '__all__') params.set('stato_pagamento', statoPagamento);
    if (search.trim()) params.set('q', search.trim());
    if (dataFrom) params.set('data_from', dataFrom);
    if (dataTo) params.set('data_to', dataTo);
    if (importoMin) params.set('importo_min', importoMin);
    if (importoMax) params.set('importo_max', importoMax);
    return params;
  }, [year, direzione, search, stato, statoPagamento, dataFrom, dataTo, importoMin, importoMax]);

  const caricaFatture = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const paramsElenco = parametriFiltro();
      paramsElenco.set('limit', String(perPagina));
      paramsElenco.set('offset', String(Math.max(0, (pagina - 1) * perPagina)));

      const [respElenco, respRiepilogo] = await Promise.all([
        fetch(`/api/v1/fatture?${paramsElenco.toString()}`, { headers: authHeaders() }),
        fetch(`/api/v1/fatture/riepilogo?${parametriFiltro().toString()}`, {
          headers: authHeaders(),
        }),
      ]);
      if (!respElenco.ok) throw new Error(`Errore ${respElenco.status}`);
      setFatture((await respElenco.json()) as Fattura[]);
      // Il riepilogo non deve far fallire l'elenco: se manca, si mostra "n.d."
      // invece di un totale inventato sulle righe caricate.
      setRiepilogo(respRiepilogo.ok ? ((await respRiepilogo.json()) as Riepilogo) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [parametriFiltro, pagina, perPagina]);

  useEffect(() => {
    void caricaFatture();
  }, [caricaFatture]);

  const fattureSenzaOrdine = fatture.filter((f) => f.abbinamento_stato === 'in_attesa_manuale');

  const conteggioTotale = riepilogo?.conteggio ?? null;
  const ultimaPagina =
    conteggioTotale === null ? pagina : Math.max(1, Math.ceil(conteggioTotale / perPagina));
  const primoDellaPagina = conteggioTotale === 0 ? 0 : (pagina - 1) * perPagina + 1;
  const ultimoDellaPagina = (pagina - 1) * perPagina + fatture.length;

  const tabButton = (value: Direzione, label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setDirezione(value)}
      className={
        'flex-1 border-b-2 px-4 py-2.5 text-sm font-medium transition ' +
        (direzione === value
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-semibold">Fatture</h1>
          <p className="text-sm text-muted-foreground">
            Gestione ciclo attivo e passivo del ramo corrente
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const exportParams = new URLSearchParams({ anno: String(year) });
              if (direzione !== 'tutte') exportParams.set('direzione', direzione);
              window.open(`/api/v1/export/fatture?${exportParams.toString()}`, '_blank');
            }}
          >
            Esporta
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/documentale/import-pec')}
          >
            Importa da PEC
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/documentale/fatture/nuova?direzione=passiva')}
          >
            + Passiva
          </Button>
          <Button
            onClick={() => navigate('/documentale/fatture/nuova?direzione=attiva')}
          >
            + Attiva
          </Button>
        </div>
      </div>

      {fattureSenzaOrdine.length > 0 && (
        <div className="space-y-2">
          {fattureSenzaOrdine.map((f) => (
            <AbbinamentoAlert key={f.id} fatturaId={f.id} />
          ))}
        </div>
      )}

      {/* Tabs direzione */}
      <Card>
        <div className="flex border-b border-border">
          {tabButton('tutte', 'Tutte')}
          {tabButton('attiva', 'Attive (vendite)')}
          {tabButton('passiva', 'Passive (acquisti)')}
        </div>

        <CardContent className="space-y-3 pt-4">
          {/* Ricerca principale */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                {[ANNO_CORRENTE - 1, ANNO_CORRENTE, ANNO_CORRENTE + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtri
              {showAdvanced ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Filtri avanzati (collassabili) */}
          {showAdvanced && (
            <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              <div>
                <label className="text-xs text-muted-foreground">Stato</label>
                <Select value={stato} onValueChange={setStato}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti</SelectItem>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="in_invio">In invio</SelectItem>
                    <SelectItem value="consegnata">Consegnata</SelectItem>
                    <SelectItem value="non_consegnata">Non consegnata</SelectItem>
                    <SelectItem value="trasmessa">Trasmessa</SelectItem>
                    <SelectItem value="scartata">Scartata</SelectItem>
                    <SelectItem value="accettata">Accettata (PA)</SelectItem>
                    <SelectItem value="rifiutata">Rifiutata (PA)</SelectItem>
                    <SelectItem value="ricevuta">Ricevuta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Pagamento</label>
                <Select value={statoPagamento} onValueChange={setStatoPagamento}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tutti</SelectItem>
                    <SelectItem value="non_pagata">Non pagata</SelectItem>
                    <SelectItem value="parzialmente_pagata">Parziale</SelectItem>
                    <SelectItem value="pagata">Pagata</SelectItem>
                    <SelectItem value="scaduta">Scaduta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Data da</label>
                <Input
                  type="date"
                  value={dataFrom}
                  onChange={(e) => setDataFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Data a</label>
                <Input
                  type="date"
                  value={dataTo}
                  onChange={(e) => setDataTo(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Importo min</label>
                  <Input
                    type="number"
                    value={importoMin}
                    onChange={(e) => setImportoMin(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Importo max</label>
                  <Input
                    type="number"
                    value={importoMax}
                    onChange={(e) => setImportoMax(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Riepilogo: conteggio e totali dell'INTERO insieme filtrato, non della pagina */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <span>
          {loading
            ? 'Caricamento...'
            : conteggioTotale === null
              ? `${fatture.length} risultati in questa pagina`
              : conteggioTotale === 0
                ? 'Nessun risultato'
                : `${primoDellaPagina}-${ultimoDellaPagina} di ${conteggioTotale} documenti`}
        </span>
        {!loading && riepilogo !== null && riepilogo.conteggio > 0 && (
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>
              Imponibile:{' '}
              <span className="font-medium text-foreground">
                {fmtEur(Number(riepilogo.totale_imponibile))}
              </span>
            </span>
            <span>
              IVA:{' '}
              <span className="font-medium text-foreground">
                {fmtEur(Number(riepilogo.totale_iva))}
              </span>
            </span>
            <span>
              Totale{' '}
              <span className="text-xs">({riepilogo.conteggio} documenti, tutti i filtri applicati)</span>:{' '}
              <span className="font-semibold text-foreground">
                {fmtEur(Number(riepilogo.totale_documenti))}
              </span>
            </span>
          </span>
        )}
        {!loading && riepilogo === null && fatture.length > 0 && (
          <span className="text-destructive">
            Totale non disponibile (riepilogo non raggiungibile)
          </span>
        )}
      </div>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {error && <p className="p-6 text-center text-sm text-destructive">{error}</p>}
          {!error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Direzione</TableHead>
                  <TableHead>{direzione === 'attiva' ? 'Cliente' : direzione === 'passiva' ? 'Fornitore' : 'Controparte'}</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Scadenza</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && fatture.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                      Nessuna fattura trovata.
                    </TableCell>
                  </TableRow>
                )}
                {fatture.map((fattura) => (
                  <TableRow
                    key={fattura.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(`/documentale/fatture/${fattura.id}?year=${fattura.year}`)
                    }
                  >
                    <TableCell className="font-mono text-xs">{fattura.numero ?? '—'}</TableCell>
                    <TableCell>{new Date(fattura.data).toLocaleDateString('it-IT')}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {fattura.tipo_documento_codice}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={fattura.direzione === 'attiva' ? 'default' : 'secondary'}
                      >
                        {fattura.direzione === 'attiva' ? 'Attiva' : 'Passiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {fattura.direzione === 'attiva'
                        ? (fattura.cessionario_denominazione ?? '—')
                        : (fattura.cedente_denominazione ?? '—')}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmtEur(fattura.importo_totale != null ? Number(fattura.importo_totale) : null)}
                    </TableCell>
                    <TableCell>
                      <StatoBadge stato={fattura.stato} />
                    </TableCell>
                    <TableCell>
                      {fattura.stato_pagamento ? (
                        <StatoPagamentoBadge stato={fattura.stato_pagamento} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {fattura.data_scadenza ? (
                        <span
                          className={
                            fattura.stato_pagamento !== 'pagata' &&
                            new Date(fattura.data_scadenza) < new Date()
                              ? 'font-medium text-destructive'
                              : ''
                          }
                        >
                          {new Date(fattura.data_scadenza).toLocaleDateString('it-IT')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginazione: senza, le fatture oltre la prima pagina erano invisibili */}
      {!error && (conteggioTotale === null || conteggioTotale > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Righe per pagina</span>
            <Select
              value={String(perPagina)}
              onValueChange={(v) => {
                setPerPagina(Number(v));
                setPagina(1);
              }}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIMENSIONI_PAGINA.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={loading || pagina <= 1}
              onClick={() => setPagina(Math.max(1, pagina - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Precedente
            </Button>
            <span className="px-1 text-muted-foreground">
              Pagina {pagina}
              {conteggioTotale !== null && ` di ${ultimaPagina}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={
                loading ||
                (conteggioTotale !== null
                  ? pagina >= ultimaPagina
                  : fatture.length < perPagina)
              }
              onClick={() => setPagina(pagina + 1)}
            >
              Successiva
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FattureList;
